import React, { useEffect } from 'react'
import { EventInfo } from '../../lib/EventInfo';
import { Offcanvas, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { IconAlertTriangleFilled, IconFileImport, IconShoppingCart } from '@tabler/icons-react';
import { BookingPayment } from '../../../../shared/types/booking/BookingPaymentType';
import clsx from 'clsx';
import styled from 'styled-components';
import { getDayDiff } from '../../../../shared/utils/dateUtils';
import { isBefore, startOfDay } from 'date-fns';

import OtaIcon from './OtaIcon';
import { getDownPaymentPercentage } from '../../utils/getPaymentPercentage';
import useShowBooking from '../../../../services/booking/hooks/useShowBooking';
import { toast } from 'sonner';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import TooltipContentNew1 from './TooltipContentNew1';
import BookingHoverTooltip from './BookingHoverTooltip';
import { useViewport } from '../../../../shared/hooks/useViewport';
import SwitchReservationModal from './SwitchReservationModal';
import SplitReservationModal from './SplitReservationModal';
import MergeReservationModal from './MergeReservationModal';
import { usePropertyReadOnly } from '../../../../shared/hooks/usePropertyReadOnly';

function getContrastingTextColor(hexColor: string): string {
    if (hexColor === null || hexColor === undefined || hexColor === '') {
        return '#000000'; // Default to black if no color is provided
    }
    // Hapus simbol # jika ada
    const hex = hexColor.replace('#', '');

    // Konversi ke RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Rumus YIQ
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;

    // Jika terang, pakai hitam. Jika gelap, pakai putih
    return yiq >= 128 ? '#000000' : '#ffffff';
}

interface EventContentProps {
    eventInfo: EventInfo;
    startDate: string;
    endDate: string;
    activeTooltipId: string | null;
    setActiveTooltipId: (id: string | null) => void;
    onUpdate: (id: string) => void;
    onFixIt: (id: string) => void;
    onComment: (id: string) => void;
    onUpdateStatus: (id: string) => void;
    onAddPayment: (id: string) => void;
    onAddExtra: (id: string) => void;
    onEditRoomMaintenance: (id: string) => void;
}

interface CustomEventProps {
    left: number;
    width: number;
    color: string;
}

// const CustomEvent = styled.div<CustomEventProps>`
//   position: relative;
//   height: 30px !important;
//   color: white;
//   text-align: center;
//   border-radius: 5px;
//   top: 3px;

//   // Adding the pseudo-element for the skew effect
//   &::before {
//     content: '';
//     position: absolute;
//     top: 0;
//     left: ${props => props.left}%;
//     width: ${props => props.width}%;
//     height: 100%;
//     transform: skewX(-25deg);
//     transform-origin: top left;
//     z-index: -1;  // Make sure the pseudo-element is behind the content
//     background-color: ${props => props.color}; /* Example background color */
//     // border-left: 3px solid #fff;  // Add border to the left
//   }
// `;

const CustomEvent = styled.div<CustomEventProps>`
  position: absolute;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  height: 30px;
  color: ${props => getContrastingTextColor(props.color)};
  text-align: center;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  background-color: ${props => props.color};
  margin-top: 2.5px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50px;
    background-color: ${props => props.color};
    opacity: 0.7;
    z-index: -1;
  }
`;

const getDisplayTitle = (title: string, maxLength: number, eventInfo: any, dayDiff: number, isFuture?: boolean, isPast?: boolean): string => {
    const today = new Date();
    const startDate = new Date(eventInfo.event.start);
    const endDate = new Date(eventInfo.event.end);

    // Jika start sudah lewat, gunakan selisih dari endDate ke hari ini
    const adjustedDayDiff = startDate < today
        ? Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        : dayDiff;

    // Pastikan minimal dayDiff adalah 1
    const finalDayDiff = Math.max(1, adjustedDayDiff);

    // Tentukan panjang substring
    const substringLength = Math.min(title.length, finalDayDiff * maxLength);

    return title.length > substringLength ? title.substring(0, ((isFuture || isPast) ? maxLength : substringLength)) + '...' : title;
};

// Komponen untuk menampilkan jika memiliki extendedProps
const BookingEvent: React.FC<EventContentProps> = ({ eventInfo, startDate, endDate, activeTooltipId, setActiveTooltipId, onUpdate, onFixIt, onComment, onUpdateStatus, onAddPayment, onAddExtra }) => {
    const { isReadOnly } = usePropertyReadOnly();
    const [tooltipShow, setTooltipShow] = React.useState(false);
    const [showSwitchModal, setShowSwitchModal] = React.useState(false);
    const [showSplitModal, setShowSplitModal] = React.useState(false);
    const [showMergeModal, setShowMergeModal] = React.useState(false);
    // Lihat komentar panjang di dekat `isHoverTooltipDisabled` di bawah — dipisah
    // dari state hover INTERNAL react-bootstrap supaya bisa dipaksa tertutup.
    const [hoverVisible, setHoverVisible] = React.useState(false);

    const { isMobile } = useViewport();

    // ====================================================================
    // PERBAIKAN: Pindahkan data fetching ke sini (ke komponen Popover)
    // ====================================================================
    const shouldFetchBooking = activeTooltipId === eventInfo.event.id;
    const { data: booking, isError, error, isSuccess, isFetching } = useShowBooking(
        shouldFetchBooking ? { bookingId: eventInfo.event.id } : { bookingId: '' }
    );

    useEffect(() => {
        if (isError && error) {
            handleCloseTooltip();
            toast.error('Failed to fetch booking details', { id: 'fetch-booking-details' });
        }
    }, [isError, error]);
    // ====================================================================

    useEffect(() => {
        if (activeTooltipId !== eventInfo.event.id) {
            setTooltipShow(false);
        }
    }, [activeTooltipId]);

    const handleActiveTooltip = () => {
        if (activeTooltipId === eventInfo.event.id) {
            setActiveTooltipId(null);
        } else {
            setActiveTooltipId(eventInfo.event.id);
            setTooltipShow(true);
        }
    };

    const handleCloseTooltip = () => {
        setTooltipShow(false);
        setActiveTooltipId(null);
    };

    // const backgroundColor = eventInfo.event.extendedProps?.source === 'direct' ? (eventInfo.event.extendedProps?.agent_color ? eventInfo.event.extendedProps?.agent_color : eventInfo.event.extendedProps?.ota_primary_color) : eventInfo.event.extendedProps?.ota_primary_color;
    const { end, start } = eventInfo.event;
    const { source, agent_color, ota_primary_color } = eventInfo.event.extendedProps ?? {};

    // Dapatkan tanggal hari ini tanpa waktu (00:00:00)
    const today = startOfDay(new Date());

    // Konversi `end` menjadi Date, pastikan tidak undefined
    const eventEndDate = end ? startOfDay(new Date(end)) : today;

    // Cek apakah event sudah lewat dari hari ini
    const isPastEvent = isBefore(eventEndDate, today);

    const backgroundColor = isPastEvent
        ? '#919191'  // Abu-abu transparan untuk event yang sudah lewat
        : source === 'direct'
            // ? (agent_color || '#076a4e')
            ? (agent_color || 'var(--tblr-primary)')  // Gunakan warna agen jika ada, jika tidak, gunakan warna default
            : ota_primary_color;

    const dayDiff = getDayDiff(eventInfo.event.start, eventInfo.event.end, startDate, endDate);
    // const left = 100 / (dayDiff + 1) / 2;
    const left = 80 / (2 * (dayDiff + 1));
    const width = 100;

    // const maxLength = eventInfo.event.extendedProps?.ota_name === 'BookingCom' ? 3 : 5;
    const isFuture = startOfDay(new Date(end ?? '')) > startOfDay(new Date(endDate));
    const isPast = startOfDay(new Date(start ?? '')) < startOfDay(new Date(startDate));
    const hasOtaName = !!eventInfo.event.extendedProps?.ota_name;
    const maxLength = hasOtaName ? 2 : 5;
    const title = eventInfo.event.title;
    // const displayTitle = title.length > maxLength ? title.substring(0, (dayDiff * maxLength)) + '...' : title;
    const displayTitle = getDisplayTitle(title, maxLength, eventInfo, dayDiff, isFuture, isPast);

    const { total_rate, down_payment } = eventInfo.event.extendedProps ?? {};

    const paymentPercentage = getDownPaymentPercentage({ total_rate: total_rate, down_payment });

    // cek apakah start dan end event itu mengenai hari ini
    let isCheckin = eventInfo.event.extendedProps?.mark === 'checked_in';
    let isCheckout = eventInfo.event.extendedProps?.mark === 'checked_out';

    const handleUpdate = (id: string) => { handleCloseTooltip(); onUpdate(id); };
    const handleFixIt = (id: string) => { handleCloseTooltip(); onFixIt(id); };
    const handleComment = (id: string) => { handleCloseTooltip(); onComment(id); };
    const handleUpdateStatus = (id: string) => { handleCloseTooltip(); onUpdateStatus(id); };
    const handleAddPayment = (id: string) => { handleCloseTooltip(); onAddPayment(id); };
    const handleAddExtra = (id: string) => { handleCloseTooltip(); onAddExtra(id); };
    const handleSwitch = (_id: string) => { if (isReadOnly) return; handleCloseTooltip(); setShowSwitchModal(true); };
    const handleSplit = (_id: string) => { if (isReadOnly) return; handleCloseTooltip(); setShowSplitModal(true); };
    const handleMerge = (_id: string) => { if (isReadOnly) return; handleCloseTooltip(); setShowMergeModal(true); };

    const ReservationPopoverContent = () => (
        <>
            {isFetching && <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FormSpinner /></div>}
            {isSuccess && booking && (
                <TooltipContentNew1
                    booking={booking}
                    onUpdate={handleUpdate}
                    onFixIt={handleFixIt}
                    onComment={handleComment}
                    onUpdateStatus={handleUpdateStatus}
                    onAddPayment={handleAddPayment}
                    onAddExtra={handleAddExtra}
                    onSwitch={handleSwitch}
                    onSplit={handleSplit}
                    onMerge={handleMerge}
                    onClose={handleCloseTooltip}
                />
            )}
        </>
    );

    // Hover tooltip dimatikan saat:
    // 1. MOBILE — satu tap memicu 'hover' DAN onClick sekaligus, sehingga tooltip
    //    hover menumpuk dengan Offcanvas detail (dua layer bertabrakan).
    // 2. Popover detail (klik) sedang terbuka — supaya keduanya tidak tampil
    //    bersamaan di desktop.
    const isHoverTooltipDisabled = isMobile || tooltipShow;

    // GOTCHA — JANGAN matikan hover dengan cara mengganti prop `trigger` OverlayTrigger
    // (mis. `trigger={isHoverTooltipDisabled ? [] : ['hover','focus']}`). Itu penyebab
    // bug tooltip "freeze"/nyangkut terbuka: `show` internal OverlayTrigger TIDAK
    // otomatis ikut ter-reset saat `trigger` berubah (lihat source react-bootstrap —
    // `show` cuma state biasa yang diubah oleh handler onMouseOver/onMouseOut, dan
    // handler itu SENGAJA tidak dipasang sama sekali kalau `trigger` tidak menyebut
    // 'hover'). Kalau tooltip kebetulan sedang terbuka (mouse masih di atas event)
    // tepat saat `trigger` berubah jadi `[]`, listener mouseleave-nya lenyap
    // bersamaan — tooltip itu PERMANEN nyangkut terbuka sampai component unmount,
    // karena tidak ada lagi listener yang bisa memicu hide.
    //
    // Fix yang aman: `trigger` dibiarkan KONSTAN, listener selalu terpasang.
    // Visibilitas dikendalikan penuh lewat `show`/`onToggle` (controlled) — begitu
    // `isHoverTooltipDisabled` menjadi true, `show` LANGSUNG false pada render
    // berikutnya, apapun status hover sebenarnya. `hoverVisible` juga di-reset
    // eksplisit lewat effect di bawah supaya hover basi tidak "muncul lagi sendiri"
    // begitu disabled-nya kembali false.
    useEffect(() => {
        if (isHoverTooltipDisabled) setHoverVisible(false);
    }, [isHoverTooltipDisabled]);

    const TriggerEvent = (
        <div onClick={handleActiveTooltip}>
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement='auto'
                // Jeda kecil supaya tooltip tidak berkedip saat kursor sekadar
                // melintasi banyak event di grid yang padat.
                delay={{ show: 300, hide: 0 }}
                show={!isHoverTooltipDisabled && hoverVisible}
                onToggle={(nextShow) => setHoverVisible(nextShow)}
                overlay={(overlayProps) => (
                    <BookingHoverTooltip
                        {...overlayProps}
                        bookingId={eventInfo.event.id}
                        data={{
                            customerName: eventInfo.event.extendedProps?.customer_name,
                            customerSurname: eventInfo.event.extendedProps?.customer_surname,
                            otaName: eventInfo.event.extendedProps?.ota_name,
                            checkinDate: eventInfo.event.extendedProps?.checkin_date,
                            checkoutDate: eventInfo.event.extendedProps?.checkout_date,
                            paymentType: eventInfo.event.extendedProps?.payment_type,
                            publicNote: eventInfo.event.extendedProps?.public_note,
                            privateNote: eventInfo.event.extendedProps?.private_note,
                            isImported: eventInfo.event.extendedProps?.is_imported,
                            hasExtra: eventInfo.event.extendedProps?.has_extra,
                            extraCount: eventInfo.event.extendedProps?.extra_count,
                        }}
                    />
                )}
            >
                <CustomEvent color={backgroundColor} left={left} width={width} className={clsx('d-flex align-items-center justify-content-center', !BookingPayment.some((payment) => payment.value === eventInfo.event.extendedProps?.payment_type) ? 'bg-danger' : '')}>
                    <div className='d-flex align-items-center w-100 h-100 px-2'>

                        {/* Kolom 1: Ikon OTA + penanda booking hasil import */}
                        <div className={clsx('flex-shrink-0 d-flex align-items-center gap-1', dayDiff > 1 ? 'me-2' : '')}>
                            {(
                                eventInfo.event.extendedProps?.rate_plan_id == null ||
                                eventInfo.event.extendedProps?.room_type_id == null
                            ) ? (
                                <IconAlertTriangleFilled
                                    size={16}
                                    className='text-warning'
                                    title={
                                        eventInfo.event.extendedProps?.room_type_id == null
                                            ? 'Needs attention: room type not assigned'
                                            : 'Needs attention: rate plan not assigned'
                                    }
                                />
                            ) : (
                                <OtaIcon otaName={eventInfo.event.extendedProps?.ota_name} size={10} />
                            )}
                            {/* Booking hasil bulk-import di channel manager. Ikon sengaja
                                tanpa warna eksplisit supaya mewarisi `currentColor` pill —
                                warna teks pill sendiri sudah dihitung kontras terhadap
                                background OTA-nya (lihat getContrastingTextColor). */}
                            {eventInfo.event.extendedProps?.is_imported && (
                                <span className='d-inline-flex' title='Imported booking'>
                                    <IconFileImport size={11} />
                                </span>
                            )}
                            {/* Booking punya minimal 1 extra/service aktif -- pola sama
                                persis dengan ikon Imported di atas (tanpa warna eksplisit,
                                warisi currentColor pill). */}
                            {eventInfo.event.extendedProps?.has_extra && (
                                <span className='d-inline-flex' title='Has extra/service'>
                                    <IconShoppingCart size={11} />
                                </span>
                            )}
                        </div>

                        {/* --- PERUBAHAN UTAMA DI SINI --- */}

                        {/* Wrapper untuk Judul dan Badge, sekarang menjadi Relative */}
                        <div className="flex-grow-1 h-100">
                            {/* Gunakan conditional rendering (ternary operator) berdasarkan dayDiff */}
                            {(dayDiff <= 2) ? (
                                // =========================================================
                                // TATA LETAK JIKA dayDiff <= 2 (VERTIKAL DENGAN BADGE ABSOLUT)
                                // =========================================================
                                <div className="d-flex flex-column justify-content-center align-items-center h-100 text-center position-relative">

                                    {/* Judul Teks (di atas) */}
                                    <span
                                        className={'text-truncate d-block'}
                                        style={{ lineHeight: '1.2', fontSize: '13px', maxWidth: '90%' }}
                                    >
                                        {displayTitle}
                                    </span>

                                    {/* Badge Persentase (di bawah) */}
                                    {paymentPercentage !== null && paymentPercentage < 100 && (
                                        <span
                                            className='badge mt-1'
                                            style={{
                                                padding: '2px 5px',
                                                fontSize: '10px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                                                color: 'inherit',
                                                fontWeight: 500
                                            }}
                                        >
                                            {paymentPercentage}%
                                        </span>
                                    )}

                                    {paymentPercentage !== null && paymentPercentage === 100 && (
                                        <span
                                            className='badge'
                                            style={{
                                                padding: '2px 5px',
                                                fontSize: '10px',
                                                backgroundColor: '#34c759',
                                                color: 'white',
                                                fontWeight: 500
                                            }}
                                        >
                                            {paymentPercentage}%
                                        </span>
                                    )}

                                    {paymentPercentage !== null && paymentPercentage > 100 && (
                                        <span
                                            className='badge'
                                            style={{
                                                padding: '2px 5px',
                                                fontSize: '10px',
                                                backgroundColor: '#ff9500',
                                                color: 'white',
                                                fontWeight: 500
                                            }}
                                        >
                                            {paymentPercentage}%
                                        </span>
                                    )}

                                    {/* --- PERUBAHAN DI SINI --- */}
                                    {/* Container untuk Badge Status (Checkin/Checkout), diposisikan secara absolut */}
                                    <div
                                        className="position-absolute d-flex flex-column"
                                        style={{ top: '4px', right: '4px' }} // Posisi di pojok kanan atas
                                    >
                                        {/* Badge: isCheckin */}
                                        {isCheckin && !isCheckout && (
                                            <span
                                                title="In-house Today"
                                                style={{
                                                    height: '10px',
                                                    width: '10px',
                                                    backgroundColor: '#34c759',
                                                    borderRadius: '50%',
                                                    border: '1px solid white',
                                                }}
                                            />
                                        )}

                                        {/* Badge: isCheckout */}
                                        {isCheckout && !isCheckin && (
                                            <span
                                                title="Checked Out Today"
                                                style={{
                                                    height: '10px',
                                                    width: '10px',
                                                    backgroundColor: '#ff3b30',
                                                    borderRadius: '50%',
                                                    border: '1px solid white',
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                            ) : (

                                // =========================================================
                                // TATA LETAK JIKA dayDiff > 2 (HORIZONTAL - TIDAK ADA PERUBAHAN)
                                // =========================================================
                                <div className="position-relative h-100">
                                    {/* Wrapper untuk judul teks agar bisa di-center vertikal */}
                                    <div className="d-flex align-items-center h-100">
                                        <span
                                            className={'text-truncate d-block text-start'}
                                            style={{ paddingRight: (isFuture || isPast) ? '0px' : '30px' }}
                                        >
                                            {displayTitle}
                                        </span>
                                    </div>

                                    {/* Container untuk Badge, diposisikan secara absolut */}
                                    <div
                                        className="position-absolute d-flex flex-column align-items-end"
                                        style={{
                                            top: '50%',
                                            right: 0,
                                            transform: 'translateY(-50%)'
                                        }}
                                    >
                                        {/* Badge Atas: isCheckin */}
                                        {isCheckin && !isCheckout && (
                                            <span
                                                title="In-house Today"
                                                style={{
                                                    height: '10px',
                                                    width: '10px',
                                                    backgroundColor: '#34c759',
                                                    borderRadius: '50%',
                                                    border: '1px solid white',
                                                    marginBottom: '2px'
                                                }}
                                            />
                                        )}

                                        {/* Badge: isCheckout */}
                                        {isCheckout && !isCheckin && (
                                            <span
                                                title="Checked Out Today"
                                                style={{
                                                    height: '10px',
                                                    width: '10px',
                                                    backgroundColor: '#ff3b30',
                                                    borderRadius: '50%',
                                                    border: '1px solid white',
                                                    marginBottom: '2px'
                                                }}
                                            />
                                        )}

                                        {/* Badge Bawah: Payment Percentage */}
                                        {paymentPercentage !== null && paymentPercentage < 100 && (
                                            <span
                                                className='badge'
                                                style={{
                                                    padding: '2px 5px',
                                                    fontSize: '10px',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                                                    color: 'inherit',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {paymentPercentage}%
                                            </span>
                                        )}

                                        {paymentPercentage !== null && paymentPercentage === 100 && (
                                            <span
                                                className='badge'
                                                style={{
                                                    padding: '2px 5px',
                                                    fontSize: '10px',
                                                    backgroundColor: '#34c759',
                                                    color: 'white',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {paymentPercentage}%
                                            </span>
                                        )}

                                        {paymentPercentage !== null && paymentPercentage > 100 && (
                                            <span
                                                className='badge'
                                                style={{
                                                    padding: '2px 5px',
                                                    fontSize: '10px',
                                                    backgroundColor: '#ff9500',
                                                    color: 'white',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {paymentPercentage}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CustomEvent>
            </OverlayTrigger>
        </div>
    );

    if (isMobile) {
        // --- TAMPILAN MOBILE ---
        return (
            <>
                {TriggerEvent}
                <Offcanvas
                    show={tooltipShow}
                    onHide={handleCloseTooltip}
                    placement="bottom"
                    style={{ height: '90vh', borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}
                    enforceFocus={false}
                >
                    <ReservationPopoverContent />
                </Offcanvas>
            </>
        );
    }

    return (
        <>
            {tooltipShow && <div className="popover-backdrop" onClick={handleCloseTooltip}></div>}
            <OverlayTrigger
                trigger={'click'}
                show={tooltipShow}
                placement='auto'
                overlay={
                    <Popover
                        id='popover-positioned-top'
                        // ======> TAMBAHKAN CLASS DI SINI <======
                        className='custom-reservation-details tooltip-final-design-wrapper'
                    >
                        <Popover.Body className='p-0'>
                            <ReservationPopoverContent />
                        </Popover.Body>
                    </Popover>
                }
            >
                {TriggerEvent}
            </OverlayTrigger>

            {showSwitchModal && (
                <SwitchReservationModal
                    show={showSwitchModal}
                    onClose={() => setShowSwitchModal(false)}
                    bookingId={eventInfo.event.id}
                />
            )}

            {showSplitModal && (
                <SplitReservationModal
                    show={showSplitModal}
                    onClose={() => setShowSplitModal(false)}
                    bookingId={eventInfo.event.id}
                />
            )}

            {showMergeModal && (
                <MergeReservationModal
                    show={showMergeModal}
                    onClose={() => setShowMergeModal(false)}
                    bookingId={eventInfo.event.id}
                />
            )}
        </>
    );

    // return (
    //     <>
    //         {tooltipShow && <div className="popover-backdrop" onClick={handleCloseTooltip}></div>}
    //         <OverlayTrigger
    //             trigger={'click'}
    //             show={tooltipShow}
    //             placement='auto'
    //             overlay={
    //                 <Popover id='popover-positioned-top' className='custom-reservation-details'>
    //                     <Popover.Body className='p-0'>
    //                         {isFetching && <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FormSpinner /></div>}
    //                         {isSuccess && booking && (
    //                             <TooltipContentNew1
    //                                 booking={booking}
    //                                 onUpdate={handleUpdate}
    //                                 onComment={handleComment}
    //                                 onUpdateStatus={handleUpdateStatus}
    //                                 onAddPayment={handleAddPayment}
    //                                 // ===============================================
    //                                 // PERBAIKAN: Teruskan fungsi close ke komponen anak
    //                                 // ===============================================
    //                                 onClose={handleCloseTooltip}
    //                             />
    //                         )}
    //                     </Popover.Body>
    //                 </Popover>
    //             }
    //         >
    //             {/* <div
    //             className={clsx('d-flex align-items-center justify-content-center mt-1 w-100 badge badge-primary text-white',
    //                 !BookingPayment.some((payment) => payment.value === eventInfo.event.extendedProps?.payment_type) ? 'bg-danger' : '')}
    //             onClick={handleActiveTooltip}
    //             style={{ height: '30px', backgroundColor: backgroundColor }}
    //         >
    //             <div className='d-flex'>
    //                 {!BookingPayment.some((payment) => payment.value === eventInfo.event.extendedProps?.payment_type) && (
    //                     <span>!! </span>
    //                 )}
    //                 <i>{eventInfo.event.title}</i>
    //             </div>
    //         </div> */}
    //             <div onClick={handleActiveTooltip}>
    //                 <OverlayTrigger trigger={['hover', 'focus']} placement='auto' overlay={
    //                     <Tooltip id='tooltip-booking-event'>
    //                         {title}
    //                     </Tooltip>
    //                 }>
    //                     <CustomEvent color={backgroundColor} left={left} width={width} className={clsx('d-flex align-items-center justify-content-center', !BookingPayment.some((payment) => payment.value === eventInfo.event.extendedProps?.payment_type) ? 'bg-danger' : '')}>
    //                         <div className='d-flex align-items-center w-100 h-100 px-2'>

    //                             {/* Kolom 1: Ikon OTA (tidak berubah) */}
    //                             <div className={clsx('flex-shrink-0', dayDiff > 1 ? 'me-2' : '')}>
    //                                 {(
    //                                     eventInfo.event.extendedProps?.rate_plan_id == null ||
    //                                     eventInfo.event.extendedProps?.room_type_id == null
    //                                 ) ? (
    //                                     <IconAlertTriangleFilled size={16} className='text-warning' />
    //                                 ) : (
    //                                     <OtaIcon otaName={eventInfo.event.extendedProps?.ota_name} size={10} />
    //                                 )}
    //                             </div>

    //                             {/* --- PERUBAHAN UTAMA DI SINI --- */}

    //                             {/* Wrapper untuk Judul dan Badge, sekarang menjadi Relative */}
    //                             <div className="flex-grow-1 h-100">
    //                                 {/* Gunakan conditional rendering (ternary operator) berdasarkan dayDiff */}
    //                                 {(dayDiff <= 2) ? (
    //                                     // =========================================================
    //                                     // TATA LETAK JIKA dayDiff <= 2 (VERTIKAL DENGAN BADGE ABSOLUT)
    //                                     // =========================================================
    //                                     <div className="d-flex flex-column justify-content-center align-items-center h-100 text-center position-relative">

    //                                         {/* Judul Teks (di atas) */}
    //                                         <span
    //                                             className={'text-truncate d-block'}
    //                                             style={{ lineHeight: '1.2', fontSize: '13px', maxWidth: '90%' }}
    //                                         >
    //                                             {displayTitle}
    //                                         </span>

    //                                         {/* Badge Persentase (di bawah) */}
    //                                         {paymentPercentage !== null && paymentPercentage < 100 && (
    //                                             <span
    //                                                 className='badge mt-1'
    //                                                 style={{
    //                                                     padding: '2px 5px',
    //                                                     fontSize: '10px',
    //                                                     backgroundColor: 'rgba(0, 0, 0, 0.25)',
    //                                                     color: 'inherit',
    //                                                     fontWeight: 500
    //                                                 }}
    //                                             >
    //                                                 {paymentPercentage}%
    //                                             </span>
    //                                         )}

    //                                         {paymentPercentage !== null && paymentPercentage === 100 && (
    //                                             <span
    //                                                 className='badge'
    //                                                 style={{
    //                                                     padding: '2px 5px',
    //                                                     fontSize: '10px',
    //                                                     backgroundColor: '#34c759',
    //                                                     color: 'white',
    //                                                     fontWeight: 500
    //                                                 }}
    //                                             >
    //                                                 {paymentPercentage}%
    //                                             </span>
    //                                         )}

    //                                         {paymentPercentage !== null && paymentPercentage > 100 && (
    //                                             <span
    //                                                 className='badge'
    //                                                 style={{
    //                                                     padding: '2px 5px',
    //                                                     fontSize: '10px',
    //                                                     backgroundColor: '#ff9500',
    //                                                     color: 'white',
    //                                                     fontWeight: 500
    //                                                 }}
    //                                             >
    //                                                 {paymentPercentage}%
    //                                             </span>
    //                                         )}

    //                                         {/* --- PERUBAHAN DI SINI --- */}
    //                                         {/* Container untuk Badge Status (Checkin/Checkout), diposisikan secara absolut */}
    //                                         <div
    //                                             className="position-absolute d-flex flex-column"
    //                                             style={{ top: '4px', right: '4px' }} // Posisi di pojok kanan atas
    //                                         >
    //                                             {/* Badge: isCheckin */}
    //                                             {isCheckin && !isCheckout && (
    //                                                 <span
    //                                                     title="In-house Today"
    //                                                     style={{
    //                                                         height: '10px',
    //                                                         width: '10px',
    //                                                         backgroundColor: '#34c759',
    //                                                         borderRadius: '50%',
    //                                                         border: '1px solid white',
    //                                                     }}
    //                                                 />
    //                                             )}

    //                                             {/* Badge: isCheckout */}
    //                                             {isCheckout && !isCheckin && (
    //                                                 <span
    //                                                     title="Checked Out Today"
    //                                                     style={{
    //                                                         height: '10px',
    //                                                         width: '10px',
    //                                                         backgroundColor: '#ff3b30',
    //                                                         borderRadius: '50%',
    //                                                         border: '1px solid white',
    //                                                     }}
    //                                                 />
    //                                             )}
    //                                         </div>
    //                                     </div>

    //                                 ) : (

    //                                     // =========================================================
    //                                     // TATA LETAK JIKA dayDiff > 2 (HORIZONTAL - TIDAK ADA PERUBAHAN)
    //                                     // =========================================================
    //                                     <div className="position-relative h-100">
    //                                         {/* Wrapper untuk judul teks agar bisa di-center vertikal */}
    //                                         <div className="d-flex align-items-center h-100">
    //                                             <span
    //                                                 className={'text-truncate d-block text-start'}
    //                                                 style={{ paddingRight: (isFuture || isPast) ? '0px' : '30px' }}
    //                                             >
    //                                                 {displayTitle}
    //                                             </span>
    //                                         </div>

    //                                         {/* Container untuk Badge, diposisikan secara absolut */}
    //                                         <div
    //                                             className="position-absolute d-flex flex-column align-items-end"
    //                                             style={{
    //                                                 top: '50%',
    //                                                 right: 0,
    //                                                 transform: 'translateY(-50%)'
    //                                             }}
    //                                         >
    //                                             {/* Badge Atas: isCheckin */}
    //                                             {isCheckin && !isCheckout && (
    //                                                 <span
    //                                                     title="In-house Today"
    //                                                     style={{
    //                                                         height: '10px',
    //                                                         width: '10px',
    //                                                         backgroundColor: '#34c759',
    //                                                         borderRadius: '50%',
    //                                                         border: '1px solid white',
    //                                                         marginBottom: '2px'
    //                                                     }}
    //                                                 />
    //                                             )}

    //                                             {/* Badge: isCheckout */}
    //                                             {isCheckout && !isCheckin && (
    //                                                 <span
    //                                                     title="Checked Out Today"
    //                                                     style={{
    //                                                         height: '10px',
    //                                                         width: '10px',
    //                                                         backgroundColor: '#ff3b30',
    //                                                         borderRadius: '50%',
    //                                                         border: '1px solid white',
    //                                                         marginBottom: '2px'
    //                                                     }}
    //                                                 />
    //                                             )}

    //                                             {/* Badge Bawah: Payment Percentage */}
    //                                             {paymentPercentage !== null && paymentPercentage < 100 && (
    //                                                 <span
    //                                                     className='badge'
    //                                                     style={{
    //                                                         padding: '2px 5px',
    //                                                         fontSize: '10px',
    //                                                         backgroundColor: 'rgba(0, 0, 0, 0.25)',
    //                                                         color: 'inherit',
    //                                                         fontWeight: 500
    //                                                     }}
    //                                                 >
    //                                                     {paymentPercentage}%
    //                                                 </span>
    //                                             )}

    //                                             {paymentPercentage !== null && paymentPercentage === 100 && (
    //                                                 <span
    //                                                     className='badge'
    //                                                     style={{
    //                                                         padding: '2px 5px',
    //                                                         fontSize: '10px',
    //                                                         backgroundColor: '#34c759',
    //                                                         color: 'white',
    //                                                         fontWeight: 500
    //                                                     }}
    //                                                 >
    //                                                     {paymentPercentage}%
    //                                                 </span>
    //                                             )}

    //                                             {paymentPercentage !== null && paymentPercentage > 100 && (
    //                                                 <span
    //                                                     className='badge'
    //                                                     style={{
    //                                                         padding: '2px 5px',
    //                                                         fontSize: '10px',
    //                                                         backgroundColor: '#ff9500',
    //                                                         color: 'white',
    //                                                         fontWeight: 500
    //                                                     }}
    //                                                 >
    //                                                     {paymentPercentage}%
    //                                                 </span>
    //                                             )}
    //                                         </div>
    //                                     </div>
    //                                 )}
    //                             </div>
    //                         </div>
    //                     </CustomEvent>
    //                 </OverlayTrigger>
    //             </div>
    //         </OverlayTrigger>
    //     </>
    // )
};

const AvailabilityEvent: React.FC<{ eventInfo: EventInfo }> = ({ eventInfo }) => (
    <div className='d-flex align-items-center justify-content-center text-black mt-1'>
        <b>{eventInfo.event.title}</b>
    </div>
);

const RoomMaintenance: React.FC<{ eventInfo: EventInfo; onEditRoomMaintenance: (id: string) => void }> = ({ eventInfo, onEditRoomMaintenance }) => {
    const dayDiff = getDayDiff(eventInfo.event.start, eventInfo.event.end);
    // const left = 100 / (dayDiff + 1) / 2;
    const left = 80 / (2 * (dayDiff + 1));
    const width = 100;

    const maxLength = 3;
    const title = eventInfo.event.title;
    const displayTitle = getDisplayTitle(title, maxLength, eventInfo, dayDiff);
    return (
        <OverlayTrigger trigger={['hover', 'focus']} placement='auto' overlay={
            <Tooltip id='tooltip-room-maintenance'>
                {title}
            </Tooltip>
        }>
            <CustomEvent
                color='#ff7a38'
                left={left}
                width={width}
                className='d-flex align-items-center justify-content-center'
                style={{ cursor: 'pointer' }}
                onClick={() => onEditRoomMaintenance(eventInfo.event.id)}
            >
                <div className='d-flex'>
                    <i>{displayTitle}</i>
                </div>
            </CustomEvent>
        </OverlayTrigger>
    );
}

const EventContent: React.FC<EventContentProps> = ({ eventInfo, startDate, endDate, activeTooltipId, setActiveTooltipId, onUpdate, onFixIt, onComment, onUpdateStatus, onAddPayment, onAddExtra, onEditRoomMaintenance }) => {
    if (eventInfo.event.extendedProps && eventInfo.event.extendedProps.type === 'booking' || eventInfo.event.extendedProps?.type === 'unasigment') {
        return (
            <BookingEvent eventInfo={eventInfo} startDate={startDate} endDate={endDate} activeTooltipId={activeTooltipId} setActiveTooltipId={setActiveTooltipId} onUpdate={onUpdate} onFixIt={onFixIt} onComment={onComment} onUpdateStatus={onUpdateStatus} onAddPayment={onAddPayment} onAddExtra={onAddExtra} onEditRoomMaintenance={onEditRoomMaintenance} />
        );
    } else if (eventInfo.event.extendedProps?.type === 'roomMaintenance') {
        return (
            <RoomMaintenance eventInfo={eventInfo} onEditRoomMaintenance={onEditRoomMaintenance} />
        );
    } else {
        return <AvailabilityEvent eventInfo={eventInfo} />;
    }
}

export default EventContent;